import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const ALLOWED_BUSINESS_ID = 23; // Surya Batteries — new mock design (v2)

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' + dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function mediaChip(type) {
  if (!type) return null;
  if (type === 'video') return <span className="media-chip vid">🎥 Video</span>;
  if (type === 'document') return <span className="media-chip pdf">📄 PDF</span>;
  return <span className="media-chip img">🖼️ Image</span>;
}

function statusBadge(row) {
  if (row.status === 'failed') {
    return <span className="badge failed">✕ Failed{row.error_reason ? ` - ${row.error_reason}` : ''}</span>;
  }
  return <span className="badge sent">✓ Sent</span>;
}

function windowBadge(row) {
  if (row.window_open) {
    return <span className="badge window">🟢 Open{row.window_hours_left ? ` · ${row.window_hours_left}h left` : ''}</span>;
  }
  return <span className="badge closed">⚪ Closed</span>;
}

/* ================= NEW MOCK DESIGN (v2) — business_id === 23 (Surya Batteries) ================= */
function MediaCampaignReports({ selectedBusiness }) {
  const [campaigns, setCampaigns] = useState([]);
  const [individual, setIndividual] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('template'); // 'template' | 'free'
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/campaigns/media-reports?business_id=${selectedBusiness.id}`);
      setCampaigns(r.data.campaigns || []);
      setIndividual(r.data.individual || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
    setLoading(false);
  }, [selectedBusiness]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isFreeMedia = (c) => c.template_name === 'Free Media';
  const templateCampaigns = campaigns.filter(c => !isFreeMedia(c));
  const freeMediaCampaigns = campaigns.filter(isFreeMedia);
  const visibleCampaigns = activeTab === 'template' ? templateCampaigns : freeMediaCampaigns;

  const totalCampaigns = visibleCampaigns.length;
  const totalSent = visibleCampaigns.reduce((s, c) => s + Number(c.sent_count || 0), 0);
  const totalFailed = visibleCampaigns.reduce((s, c) => s + Number(c.failed_count || 0), 0);
  const visibleCampaignIds = new Set(visibleCampaigns.map(c => c.id));
  const windowOpenNow = new Set(
    individual.filter(r => r.window_open && visibleCampaignIds.has(r.campaign_id)).map(r => r.contact_id)
  ).size;

  const individualForCampaign = (campaignId) => individual.filter(r => r.campaign_id === campaignId);
  const campaignNameById = (id) => campaigns.find(c => c.id === id)?.campaign_name || '-';

  if (loading) return <div style={{ padding: 24 }}>Loading reports…</div>;
  if (error) return <div style={{ padding: 24, color: '#dc2626' }}>Error: {error}</div>;

  return (
    <div className="cr-root">
      <style>{`
        .cr-root{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1f2937;}
        .cr-root h1{font-size:22px;margin:0 0 4px;}
        .cr-sub{color:#8b8fa3;font-size:13.5px;margin:0 0 22px;}
        .cr-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:24px;}
        .cr-stat{background:#fff;border:1px solid #e8e9f0;border-radius:14px;padding:16px 18px;}
        .cr-stat .num{font-size:26px;font-weight:800;line-height:1.1;}
        .cr-stat .lbl{font-size:12px;color:#8b8fa3;margin-top:4px;}
        .cr-stat.total .num{color:#7c3aed;}
        .cr-stat.sent .num{color:#16a34a;}
        .cr-stat.failed .num{color:#dc2626;}
        .cr-stat.window .num{color:#ca8a04;}
        .cr-tabs{display:flex;gap:4px;background:#eceef5;padding:4px;border-radius:12px;width:fit-content;margin-bottom:20px;}
        .cr-tab{padding:9px 18px;border-radius:9px;font-size:13.5px;font-weight:600;color:#8b8fa3;cursor:pointer;border:none;background:none;}
        .cr-tab.active{background:#fff;color:#5b21b6;box-shadow:0 1px 3px rgba(0,0,0,.08);}
        .cr-panel{background:#fff;border:1px solid #e8e9f0;border-radius:14px;overflow:hidden;}
        .cr-panel table{width:100%;border-collapse:collapse;font-size:13px;}
        .cr-panel th{text-align:left;color:#8b8fa3;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:.4px;padding:12px 16px;background:#fafafe;border-bottom:1px solid #e8e9f0;}
        .cr-panel td{padding:13px 16px;border-bottom:1px solid #f2f3f8;vertical-align:middle;}
        .cr-panel tr:last-child td{border-bottom:none;}
        .campaign-row{cursor:pointer;}
        .campaign-row:hover{background:#fafafe;}
        .campaign-name{font-weight:600;font-size:13.5px;}
        .campaign-date{color:#8b8fa3;font-size:11.5px;margin-top:2px;}
        .status-pills span{display:inline-block;margin-right:10px;font-size:12px;}
        .dot{width:7px;height:7px;border-radius:50%;display:inline-block;margin-right:5px;}
        .dot.green{background:#16a34a;} .dot.red{background:#dc2626;}
        .chevron{color:#8b8fa3;font-size:16px;}
        .expand-row td{background:#faf9ff;padding:0;}
        .expand-inner{padding:14px 16px 16px 52px;max-height:340px;overflow-y:auto;}
        .contact-line{display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px dashed #e5e0f5;font-size:12.5px;gap:12px;flex-wrap:wrap;}
        .contact-line:last-child{border-bottom:none;}
        .avatar{width:22px;height:22px;border-radius:50%;background:#7c3aed;color:#fff;font-size:10px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;margin-right:8px;}
        .badge{padding:2px 8px;border-radius:999px;font-size:11px;font-weight:700;}
        .badge.window{background:#fefce8;color:#ca8a04;}
        .badge.closed{background:#f1f2f6;color:#8b8fa3;}
        .badge.sent{background:#f0fdf4;color:#16a34a;}
        .badge.failed{background:#fef2f2;color:#dc2626;}
        .media-chip{display:inline-flex;align-items:center;gap:4px;padding:3px 9px;border-radius:7px;font-size:11.5px;font-weight:600;}
        .media-chip.img{background:#eef2ff;color:#4338ca;}
        .media-chip.vid{background:#fdf2f8;color:#be185d;}
        .media-chip.pdf{background:#fff7ed;color:#c2410c;}
        .cr-empty{padding:24px;text-align:center;color:#8b8fa3;font-size:13px;}
        .cr-individual-title{font-size:15px;margin:24px 0 12px;font-weight:700;}
      `}</style>

      <h1>Campaign Reports</h1>
      <p className="cr-sub">{selectedBusiness?.name || ''} · Media Campaign performance</p>

      <div className="cr-stats">
        <div className="cr-stat total"><div className="num">{totalCampaigns}</div><div className="lbl">{activeTab === 'template' ? 'Template Campaigns' : 'Free Media Sends'}</div></div>
        <div className="cr-stat sent"><div className="num">{totalSent}</div><div className="lbl">Delivered</div></div>
        <div className="cr-stat failed"><div className="num">{totalFailed}</div><div className="lbl">Failed {activeTab === 'template' ? '(Template)' : '(Free Media)'}</div></div>
        <div className="cr-stat window"><div className="num">{windowOpenNow}</div><div className="lbl">Windows Open Now</div></div>
      </div>

      <div className="cr-tabs">
        <button className={`cr-tab ${activeTab === 'template' ? 'active' : ''}`} onClick={() => { setActiveTab('template'); setExpandedId(null); }}>📨 Template Campaigns</button>
        <button className={`cr-tab ${activeTab === 'free' ? 'active' : ''}`} onClick={() => { setActiveTab('free'); setExpandedId(null); }}>💬 Free Media Sends</button>
      </div>

      <div className="cr-panel">
        {visibleCampaigns.length === 0 ? (
          <div className="cr-empty">No campaigns yet in this tab.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Campaign</th><th>Template</th><th>Media</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {visibleCampaigns.map(c => (
                <React.Fragment key={c.id}>
                  <tr className="campaign-row" onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}>
                    <td>
                      <div className="campaign-name">{c.campaign_name}</div>
                      <div className="campaign-date">{fmtDate(c.schedule_time)}</div>
                    </td>
                    <td>{c.template_name}</td>
                    <td>{mediaChip(c.media_type)}</td>
                    <td className="status-pills">
                      <span><span className="dot green"></span>{c.sent_count || 0} sent</span>
                      <span><span className="dot red"></span>{c.failed_count || 0} failed</span>
                    </td>
                    <td className="chevron">{expandedId === c.id ? '▾' : '▸'}</td>
                  </tr>
                  {expandedId === c.id && (
                    <tr className="expand-row">
                      <td colSpan={5}>
                        <div className="expand-inner">
                          {individualForCampaign(c.id).length === 0 ? (
                            <div style={{ color: '#8b8fa3', fontSize: 12.5 }}>No per-contact records found.</div>
                          ) : individualForCampaign(c.id).map(row => (
                            <div className="contact-line" key={row.id}>
                              <div>
                                <span className="avatar">{(row.name || '?').charAt(0).toUpperCase()}</span>
                                <b>{row.name}</b> · {row.phone}
                              </div>
                              <div>
                                {mediaChip(row.media_type)} {statusBadge(row)} &nbsp; {windowBadge(row)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Individual Contact Records — full flat table, per v2 mock */}
      <div className="cr-individual-title">👤 Individual Contact Records</div>
      <div className="cr-panel">
        {individual.length === 0 ? (
          <div className="cr-empty">No individual contact records yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Contact</th><th>Campaign</th><th>Template</th><th>Media</th><th>Status</th><th>Window</th>
              </tr>
            </thead>
            <tbody>
              {individual.map(row => (
                <tr key={row.id}>
                  <td>
                    <span className="avatar">{(row.name || '?').charAt(0).toUpperCase()}</span>
                    <b>{row.name}</b>
                    <br />
                    <span style={{ color: '#8b8fa3', fontSize: 11, marginLeft: 30 }}>{row.phone}</span>
                  </td>
                  <td>{row.campaign_name || campaignNameById(row.campaign_id)}</td>
                  <td>{row.template_name}</td>
                  <td>{mediaChip(row.media_type)}</td>
                  <td>{statusBadge(row)}</td>
                  <td>{windowBadge(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ================= ORIGINAL DESIGN — all other businesses ================= */
function ShopifyStatCard({ title, badgeStyle, deliveredColor, readColor, barDelivered, barRead, stats }) {
  const pct = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0;
  const deliveredPct = pct(stats.delivered, stats.sent);
  const readPct = pct(stats.read, stats.delivered);
  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px 20px', border: '1.5px solid #e8ebf5', position: 'relative', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: barDelivered, borderRadius: '14px 14px 0 0' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, marginTop: 4 }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: '#1a1d2e' }}>{title}</span>
        <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 5, padding: '3px 9px', letterSpacing: '0.04em', ...badgeStyle }}>Shopify</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div style={{ textAlign: 'center', paddingRight: 4, borderRight: '1px solid #f0f2f8' }}>
          <div style={{ fontSize: 10, color: '#9098b5', fontWeight: 500, marginBottom: 4, letterSpacing: '0.03em', textTransform: 'uppercase' }}>Sent</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#1a1d2e', lineHeight: 1 }}>{stats.sent}</div>
          <div style={{ height: 3, borderRadius: 2, background: '#e2e8f0', marginTop: 7 }} />
        </div>
        <div style={{ textAlign: 'center', padding: '0 4px', borderRight: '1px solid #f0f2f8' }}>
          <div style={{ fontSize: 10, color: '#9098b5', fontWeight: 500, marginBottom: 4, letterSpacing: '0.03em', textTransform: 'uppercase' }}>Delivered</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: deliveredColor, lineHeight: 1 }}>{stats.delivered}</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: deliveredColor, marginTop: 3 }}>{deliveredPct}%</div>
          <div style={{ height: 3, borderRadius: 2, background: '#e2e8f0', marginTop: 7, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: deliveredPct + '%', background: barDelivered }} />
          </div>
        </div>
        <div style={{ textAlign: 'center', paddingLeft: 4 }}>
          <div style={{ fontSize: 10, color: '#9098b5', fontWeight: 500, marginBottom: 4, letterSpacing: '0.03em', textTransform: 'uppercase' }}>Read</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: readColor, lineHeight: 1 }}>{stats.read}</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: readColor, marginTop: 3 }}>{readPct}%</div>
          <div style={{ height: 3, borderRadius: 2, background: '#e2e8f0', marginTop: 7, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: readPct + '%', background: barRead }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- NEW: Overall Total block (sent/delivered/read summed across the 3 Shopify events) ---- */
function ShopifyOverallTotal({ orderStats, fulfillStats, cartStats }) {
  const totalSent = orderStats.sent + fulfillStats.sent + cartStats.sent;
  const totalDelivered = orderStats.delivered + fulfillStats.delivered + cartStats.delivered;
  const totalRead = orderStats.read + fulfillStats.read + cartStats.read;

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #e8ebf5', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '20px 24px', marginTop: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: '#9098b5', textTransform: 'uppercase', marginBottom: 16 }}>
        Overall Total — All Campaigns Combined
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div style={{ textAlign: 'center', borderRight: '1px solid #f0f2f8' }}>
          <div style={{ fontSize: 12, color: '#9098b5', marginBottom: 6 }}>Total sent</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#1a1d2e' }}>{totalSent}</div>
        </div>
        <div style={{ textAlign: 'center', borderRight: '1px solid #f0f2f8' }}>
          <div style={{ fontSize: 12, color: '#9098b5', marginBottom: 6 }}>Total delivered</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#16a34a' }}>{totalDelivered}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#9098b5', marginBottom: 6 }}>Total read</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#2563eb' }}>{totalRead}</div>
        </div>
      </div>
    </div>
  );
}

/* ---- NEW: Automatic message stats table ---- */
function ShopifyStatsTable({ orderStats, fulfillStats, cartStats }) {
  const pct = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0;

  const rows = [
    { label: 'Order creation', color: '#16a34a', stats: orderStats },
    { label: 'Order fulfillment', color: '#2563eb', stats: fulfillStats },
    { label: 'Checkout creation', color: '#ea580c', stats: cartStats },
  ];

  const totalSent = orderStats.sent + fulfillStats.sent + cartStats.sent;
  const totalDelivered = orderStats.delivered + fulfillStats.delivered + cartStats.delivered;
  const totalRead = orderStats.read + fulfillStats.read + cartStats.read;

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #e8ebf5', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '20px 24px', marginTop: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1d2e', marginBottom: 14 }}>Automatic message stats</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {['Type', 'Sent', 'Delivered', 'Read', 'Delivery %', 'Read %'].map((h, i) => (
              <th key={h} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '8px 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', color: '#9098b5', textTransform: 'uppercase', borderBottom: '1px solid #f0f2f8' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const dPct = pct(r.stats.delivered, r.stats.sent);
            const rPct = pct(r.stats.read, r.stats.sent);
            return (
              <tr key={r.label} style={{ borderBottom: '1px solid #f6f7fb' }}>
                <td style={{ padding: '10px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 12.5, color: r.color, background: r.color + '14', borderRadius: 999, padding: '3px 10px' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: r.color, display: 'inline-block' }} />
                    {r.label}
                  </span>
                </td>
                <td style={{ padding: '10px', textAlign: 'right', color: '#374151' }}>{r.stats.sent}</td>
                <td style={{ padding: '10px', textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>{r.stats.delivered}</td>
                <td style={{ padding: '10px', textAlign: 'right', color: r.color, fontWeight: 600 }}>{r.stats.read}</td>
                <td style={{ padding: '10px', textAlign: 'right' }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, background: '#f0fdf4', color: '#16a34a', borderRadius: 6, padding: '2px 8px' }}>{dPct}%</span>
                </td>
                <td style={{ padding: '10px', textAlign: 'right' }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, background: '#f1f2f6', color: '#6b7280', borderRadius: 6, padding: '2px 8px' }}>{rPct}%</span>
                </td>
              </tr>
            );
          })}
          <tr>
            <td style={{ padding: '12px 10px', fontWeight: 700, color: '#1a1d2e' }}>Total</td>
            <td style={{ padding: '12px 10px', textAlign: 'right', fontWeight: 700, color: '#1a1d2e' }}>{totalSent}</td>
            <td style={{ padding: '12px 10px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>{totalDelivered}</td>
            <td style={{ padding: '12px 10px', textAlign: 'right', fontWeight: 700, color: '#2563eb' }}>{totalRead}</td>
            <td style={{ padding: '12px 10px', textAlign: 'right' }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, background: '#dcfce7', color: '#15803d', borderRadius: 6, padding: '2px 8px' }}>{pct(totalDelivered, totalSent)}%</span>
            </td>
            <td style={{ padding: '12px 10px', textAlign: 'right' }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8', borderRadius: 6, padding: '2px 8px' }}>{pct(totalRead, totalSent)}%</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function OriginalCampaignReports({ selectedBusiness }) {
  const [campaigns, setCampaigns] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failedModal, setFailedModal] = useState(null);
  const [failedList, setFailedList] = useState([]);
  const [failedLoading, setFailedLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('history');
  const [orderStats, setOrderStats] = useState({ sent: 0, delivered: 0, read: 0 });
  const [fulfillStats, setFulfillStats] = useState({ sent: 0, delivered: 0, read: 0 });
  const [cartStats, setCartStats] = useState({ sent: 0, delivered: 0, read: 0 });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedBusiness?.id) {
      fetchAll();
      const interval = setInterval(fetchAll, 30000);
      return () => clearInterval(interval);
    }
  }, [selectedBusiness]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get(`/campaigns?business_id=${selectedBusiness.id}`),
        api.get(`/campaigns/scheduled?business_id=${selectedBusiness.id}`).catch(() => ({ data: [] }))
      ]);
      setCampaigns(Array.isArray(c.data) ? c.data : []);
      setScheduled(Array.isArray(s.data) ? s.data : []);
      const r = await api.get(`/messages/msg-stats?business_id=${selectedBusiness.id}`).catch(() => ({ data: {} }));
      const st = r.data || {};
      setOrderStats({ sent: Number(st.order_sent) || 0, delivered: Number(st.order_delivered) || 0, read: Number(st.order_read) || 0 });
      setFulfillStats({ sent: Number(st.fulfillment_sent) || 0, delivered: Number(st.fulfillment_delivered) || 0, read: Number(st.fulfillment_read) || 0 });
      setCartStats({ sent: Number(st.cart_sent) || 0, delivered: Number(st.cart_delivered) || 0, read: Number(st.cart_read) || 0 });
    } catch (e) {}
    setLoading(false);
  };

  const fetchFailed = async (campaign) => {
    setFailedModal(campaign);
    setFailedLoading(true);
    try {
      const r = await api.get(`/campaigns/${campaign.id}/failed`);
      setFailedList(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setFailedList([]); }
    setFailedLoading(false);
  };

  const shopifyTotalSent = orderStats.sent + fulfillStats.sent + cartStats.sent;
  const hasShopifyData = shopifyTotalSent > 0;

  return (
    <>
      <div style={{ padding: '28px 32px', fontFamily: 'DM Sans, sans-serif', background: '#f4f6f9', minHeight: '100vh' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111', margin: 0 }}>Campaign Reports</h2>
          <button onClick={fetchAll} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 15, fontWeight: 600 }}>🔄 Refresh</button>
        </div>

        {hasShopifyData && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 12 }}>🛒 Shopify Events</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
              <ShopifyStatCard
                title="Order creation"
                badgeStyle={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}
                deliveredColor="#16a34a"
                readColor="#2563eb"
                barDelivered="#22c55e"
                barRead="#3b82f6"
                stats={orderStats}
              />
              <ShopifyStatCard
                title="Order fulfillment"
                badgeStyle={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}
                deliveredColor="#2563eb"
                readColor="#7c3aed"
                barDelivered="#3b82f6"
                barRead="#7c3aed"
                stats={fulfillStats}
              />
              <ShopifyStatCard
                title="Checkout creation"
                badgeStyle={{ background: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa' }}
                deliveredColor="#16a34a"
                readColor="#ea580c"
                barDelivered="#22c55e"
                barRead="#f97316"
                stats={cartStats}
              />
            </div>

            {/* NEW: Overall Total + Automatic message stats table */}
            <ShopifyOverallTotal orderStats={orderStats} fulfillStats={fulfillStats} cartStats={cartStats} />
            <ShopifyStatsTable orderStats={orderStats} fulfillStats={fulfillStats} cartStats={cartStats} />
          </div>
        )}

      </div>

      {failedModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '90%', maxWidth: 600, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontWeight: 700, fontSize: 16, margin: 0 }}>Failed — {failedModal.campaign_name}</h3>
              <button onClick={() => setFailedModal(null)} style={{ border: 'none', background: '#f3f4f6', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 15, fontWeight: 600 }}>Close</button>
            </div>
            {failedLoading ? <p style={{ color: '#6b7280', textAlign: 'center' }}>Loading...</p> : failedList.length === 0 ? (
              <p style={{ color: '#6b7280', textAlign: 'center' }}>No failed messages found.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#f9fafb' }}>
                  {['Phone', 'Name', 'Reason'].map(h => <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 15, textTransform: 'uppercase' }}>{h}</th>)}
                </tr></thead>
                <tbody>{failedList.map((m, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{m.phone}</td>
                    <td style={{ padding: '10px 14px', color: '#374151' }}>{m.contact_name || '-'}</td>
                    <td style={{ padding: '10px 14px', color: '#ef4444' }}>{m.error_reason || 'Unknown'}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* ================= SWITCHER ================= */
export default function CampaignReports() {
  const { selectedBusiness } = useAuth();

  if (selectedBusiness?.id && Number(selectedBusiness.id) === ALLOWED_BUSINESS_ID) {
    return <MediaCampaignReports selectedBusiness={selectedBusiness} />;
  }
  return <OriginalCampaignReports selectedBusiness={selectedBusiness} />;
}
